import React, { FC, useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Info } from "feather-icons-react";
import { useTranslation } from "react-i18next";

import type { PayDifferenceData, PayDifferenceDataMultiple } from "../../hooks";

import { Card, CardContent } from "@components/base";
import { CircularGauge } from "@components/common/charts";

interface PaygapPayDifferenceGaugeProps {
  payDifferenceData: PayDifferenceData;
  payDifferenceDataMultiple?: PayDifferenceDataMultiple;
}

const PaygapPayDifferenceGauge: FC<PaygapPayDifferenceGaugeProps> = ({
  payDifferenceData,
  payDifferenceDataMultiple,
}) => {
  const { t } = useTranslation();
  const [selectedClassification, setSelectedClassification] = useState<string>("1");

  // Determine available classifications
  const availableClassifications: Array<{ key: string; label: string }> = [];

  if (payDifferenceDataMultiple?.classification1) {
    availableClassifications.push({
      key: "1",
      label: t("paygap.gauge.classification1"),
    });
  }
  if (payDifferenceDataMultiple?.classification2) {
    availableClassifications.push({
      key: "2",
      label: t("paygap.gauge.classification2"),
    });
  }
  if (payDifferenceDataMultiple?.classification3) {
    availableClassifications.push({
      key: "3",
      label: t("paygap.gauge.classification3"),
    });
  }

  // Get current data based on selection
  const getCurrentData = (): PayDifferenceData => {
    if (!payDifferenceDataMultiple) return payDifferenceData;

    switch (selectedClassification) {
      case "2":
        return payDifferenceDataMultiple.classification2 ?? payDifferenceData;
      case "3":
        return payDifferenceDataMultiple.classification3 ?? payDifferenceData;
      default:
        return payDifferenceDataMultiple.classification1 ?? payDifferenceData;
    }
  };

  const currentData = getCurrentData();
  const showSwitcher = availableClassifications.length > 1;

  const handleClassificationChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null,
  ): void => {
    if (newValue !== null) {
      setSelectedClassification(newValue);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {t("paygap.analysis.payDifference.title")}
          </Typography>
          {showSwitcher && (
            <ToggleButtonGroup
              value={selectedClassification}
              exclusive
              onChange={handleClassificationChange}
              size="small"
            >
              {availableClassifications.map((classification) => (
                <ToggleButton key={classification.key} value={classification.key}>
                  {classification.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        <Box id="paygap-pay-difference-gauge" display="flex" alignItems="center" gap={4}>
          <CircularGauge value={currentData.genderCoefficient} />

          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6" color="text.primary">
              {currentData.genderCoefficient === 0
                ? t("paygap.analysis.payDifference.noDifference")
                : currentData.genderCoefficient > 0
                  ? t("paygap.analysis.payDifference.menEarnLess")
                  : t("paygap.analysis.payDifference.womenEarnLess")}
            </Typography>

            <Typography variant="body2">
              {t("paygap.analysis.payDifference.statistical")}{" "}
              {currentData.isSignificant
                ? t("paygap.analysis.payDifference.significant")
                : t("paygap.analysis.payDifference.notSignificant")}
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              p={2}
              bgcolor="grey.50"
              borderRadius={1}
            >
              <Info size={16} />
              <Typography variant="body2">
                <strong>
                  {t("paygap.analysis.payDifference.calculationMethod")}
                </strong>{" "}
                {t("paygap.analysis.payDifference.calculationDescription")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaygapPayDifferenceGauge;
